import React, { useEffect, useState } from 'react';
import { Box, Heading, Meter, Page, PageContent, Text } from 'grommet';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { API, RecentActivity } from '@enrich/shared-lib';
import QueryString from 'qs';

const CANDIDACY_STATUSES = [
    { label: 'interested', color: 'status-ok' },
    { label: 'missed_apply_deadline', color: 'status-warning' },
    { label: 'applied', color: 'brand' },
    { label: 'application_rejected', color: 'status-critical' },
    { label: 'assessment', color: 'accent-1' },
    { label: 'interview', color: 'accent-2' },
    { label: 'second_interview', color: 'accent-3' },
    { label: 'offer_received', color: 'accent-4' },
    { label: 'offer_accepted', color: 'graph-1' },
    { label: 'offer_rejected', color: 'graph-2' },
];

const PIPELINE_CANDIDACY_STATUSES = [
    ...CANDIDACY_STATUSES.map((s) => s.label),
    'closed',
];

export const Dashboard = () => {
    const user = useOutletContext();
    const company = user?.company;
    const { companySlug } = useParams();
    const navigate = useNavigate();

    const [openJobCount, setOpenJobCount] = useState(0);
    const [breakdown, setBreakdown] = useState({ total: 0, stages: [] });
    const [preparingJobCount, setPreparingJobCount] = useState(0);
    const [preparingCandidacyCount, setPreparingCandidacyCount] = useState(0);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const [
                openJobs,
                candidacyBreakdown,
                preparingJobs,
                preparingCandidacies,
            ] = await Promise.all([
                loadOpenJobCount(companySlug),
                loadCandidacyBreakdown(companySlug),
                loadPreparingJobCount(companySlug),
                loadPreparingCandidacyCount(companySlug),
            ]);

            if (isActive) {
                setOpenJobCount(openJobs);
                setBreakdown(candidacyBreakdown);
                setPreparingJobCount(preparingJobs);
                setPreparingCandidacyCount(preparingCandidacies);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [companySlug]);

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Heading level="3" margin="none">
                    {company?.name || companySlug} Dashboard
                </Heading>

                <Box direction="row" gap="medium" wrap>
                    <StatCard
                        label="Active Jobs"
                        value={openJobCount}
                        onClick={() =>
                            navigate(`/${companySlug}/jobs`, {
                                state: { statusFilter: ['open'] },
                            })
                        }
                    />
                    <StatCard
                        label="Active Candidates"
                        value={breakdown.total}
                        onClick={() => navigate(`/${companySlug}/candidates`)}
                    />
                    <StatCard
                        label="Future Jobs"
                        value={preparingJobCount}
                        onClick={() =>
                            navigate(`/${companySlug}/jobs`, {
                                state: { statusFilter: ['preparing'] },
                            })
                        }
                    />
                    <StatCard
                        label="Future Candidates"
                        value={preparingCandidacyCount}
                        onClick={() =>
                            navigate(`/${companySlug}/jobs`, {
                                state: { statusFilter: ['preparing'] },
                            })
                        }
                    />
                </Box>

                {breakdown.total > 0 && (
                    <Box
                        gap="small"
                        pad="medium"
                        elevation="small"
                        round="small">
                        <Text weight="bold">Pipeline Overview</Text>
                        <Meter
                            type="bar"
                            size="full"
                            thickness="medium"
                            max={breakdown.total}
                            values={breakdown.stages.map((stage) => ({
                                value: stage.count,
                                color: stage.color,
                                label: stage.label,
                            }))}
                        />
                        <Box
                            direction="row"
                            wrap
                            gap="small"
                            margin={{ top: 'small' }}>
                            {breakdown.stages.map((stage) => (
                                <Box
                                    key={stage.label}
                                    direction="row"
                                    align="center"
                                    gap="xsmall">
                                    <Box
                                        background={stage.color}
                                        pad="xsmall"
                                        round="xsmall"
                                        width="12px"
                                        height="12px"
                                    />
                                    <Text size="small">
                                        {formatLabel(stage.label)} (
                                        {stage.count})
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <RecentActivity
                    companySlug={companySlug}
                    contactHref={(contact) =>
                        `/${companySlug}/candidates/${contact.id}`
                    }
                />
            </PageContent>
        </Page>
    );
};

const StatCard = ({ label, value, onClick }) => (
    <Box
        pad="medium"
        elevation="small"
        round="small"
        align="center"
        gap="small"
        width="small"
        onClick={onClick}
        focusIndicator={true}
        style={{ cursor: 'pointer' }}>
        <Text size="xxlarge" weight="bold">
            {value}
        </Text>
        <Text size="small">{label}</Text>
    </Box>
);

const formatLabel = (str) =>
    str
        ?.split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ') ?? '';

const loadOpenJobCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            company: { slug: { $eqi: companySlug } },
            status: 'open',
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadCandidacyBreakdown = async (companySlug) => {
    const results = await Promise.all(
        CANDIDACY_STATUSES.map(async (statusDef) => {
            const query = QueryString.stringify({
                filters: {
                    status: statusDef.label,
                    job: {
                        status: 'open',
                        company: { slug: { $eqi: companySlug } },
                    },
                },
                pagination: { pageSize: 1 },
            });
            try {
                const response = await API.get(`/api/candidacies?${query}`);
                return {
                    ...statusDef,
                    count: response.data.meta.pagination.total,
                };
            } catch (error) {
                console.log(error);
                return { ...statusDef, count: 0 };
            }
        }),
    );

    const stages = results.filter((s) => s.count > 0);
    const total = results.reduce((sum, s) => sum + s.count, 0);
    return { total, stages };
};

const loadPreparingJobCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            company: { slug: { $eqi: companySlug } },
            status: 'preparing',
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadPreparingCandidacyCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            status: {
                $in: PIPELINE_CANDIDACY_STATUSES,
            },
            job: {
                status: 'preparing',
                company: { slug: { $eqi: companySlug } },
            },
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/candidacies?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};
